import MapboxDraw from '@mapbox/mapbox-gl-draw'
import { Feature } from 'geojson'
import { forwardRef, useEffect, useImperativeHandle } from 'react'
import { useControl } from 'react-map-gl'
import type { ControlPosition } from 'react-map-gl'

const orangeStyles = [
  {
    id: 'gl-draw-polygon-fill',
    type: 'fill',
    filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
    paint: {
      'fill-color': '#FFA500',
      'fill-outline-color': '#FFA500',
      'fill-opacity': 0.2,
    },
  },
  {
    id: 'gl-draw-polygon-stroke-active',
    type: 'line',
    filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': '#FFA500',
      'line-width': 2,
    },
  },
  {
    id: 'gl-draw-polygon-and-line-vertex-inactive',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
    paint: {
      'circle-radius': 6,
      'circle-color': '#FFA500',
    },
  },
  {
    id: 'gl-draw-vertex-selected',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['==', 'active', 'true']],
    paint: {
      'circle-radius': 8,
      'circle-color': '#FFFFFF',
    },
  },
  {
    id: 'gl-draw-polygon-midpoint',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
    paint: {
      'circle-radius': 5,
      'circle-color': '#FFA500',
    },
  },
  {
    id: 'gl-draw-line-inactive',
    type: 'line',
    filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': '#FFA500',
      'line-width': 2,
    },
  },
  {
    id: 'gl-draw-line-active',
    type: 'line',
    filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': '#FFA500',
      'line-width': 2,
    },
  },
]

type DrawControlProps = ConstructorParameters<typeof MapboxDraw>[0] & {
  position?: ControlPosition
  isEditing: boolean
  onCreate?: (evt: { features: Feature[] }) => void
  onUpdate?: (evt: { features: Feature[]; action: string }) => void
  onDelete?: (evt: { features: Feature[] }) => void
  onSelectionChange?: (evt: { features: Feature[] }) => void
  onModeChange?: (evt: { mode: string }) => void
}

const RobotMapDrawControl = forwardRef<MapboxDraw, DrawControlProps>((props, ref) => {
  const control = useControl<any>(
    () =>
      new MapboxDraw({
        ...props,
        displayControlsDefault: false,
        styles: orangeStyles,
      }),
    ({ map }: { map: any }) => {
      if (props.onCreate) map.on('draw.create', props.onCreate)
      if (props.onUpdate) map.on('draw.update', props.onUpdate)
      if (props.onDelete) map.on('draw.delete', props.onDelete)
      if (props.onSelectionChange) map.on('draw.selectionchange', props.onSelectionChange)
      if (props.onModeChange) map.on('draw.modechange', props.onModeChange)
    },
    ({ map }: { map: any }) => {
      if (props.onCreate) map.off('draw.create', props.onCreate)
      if (props.onUpdate) map.off('draw.update', props.onUpdate)
      if (props.onDelete) map.off('draw.delete', props.onDelete)
      if (props.onSelectionChange) map.off('draw.selectionchange', props.onSelectionChange)
      if (props.onModeChange) map.off('draw.modechange', props.onModeChange)
    },
    {
      position: props.position,
    }
  ) as MapboxDraw

  useImperativeHandle(ref, () => control)

  useEffect(() => {
    if (!props.isEditing) {
      control.deleteAll()
      control.changeMode('simple_select')
    }
  }, [props.isEditing, control])

  return null
})

export default RobotMapDrawControl
